import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import AdminLayout from "@/pages/admin/AdminLayout";
import { Loader2, Bell, Clock, Users, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-CL", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return "—"; }
}

export default function AdminRecordatorios() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/entry-reminders"); setData(data); }
      catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    })();
  }, []);

  if (!data) return <AdminLayout><div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="flex items-center gap-3 mb-2">
        <Bell className="w-7 h-7 text-accent" />
        <h1 className="text-2xl font-bold">Recordatorios de ingreso</h1>
      </div>
      <p className="text-muted-foreground mb-6">Solicitudes en validación de ingreso pendientes de resolución. Se envía un aviso a becas@ a las 72h y luego cada 24h hasta dar OK o rechazar.</p>

      {data.cases.length === 0 ? (
        <div className="bg-white rounded-2xl border border-border p-10 text-center" data-testid="reminders-empty">
          <Clock className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No hay validaciones de ingreso pendientes. ¡Todo al día!</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="reminders-table">
              <thead>
                <tr className="text-left text-xs uppercase text-slate-500 border-b border-border bg-secondary/40">
                  <th className="py-3 px-4">Solicitud</th>
                  <th className="py-3 px-4">Ronda</th>
                  <th className="py-3 px-4">Pendiente hace</th>
                  <th className="py-3 px-4">Avisos enviados</th>
                  <th className="py-3 px-4">Último aviso</th>
                  <th className="py-3 px-4">Próximo aviso</th>
                  <th className="py-3 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {data.cases.map((c) => {
                  const overdue = (c.days_pending || 0) >= 3;
                  return (
                    <tr key={c.id} className="border-b border-border/60 hover:bg-secondary/30" data-testid={`reminder-row-${c.id}`}>
                      <td className="py-3 px-4">
                        <p className="font-semibold text-primary">{c.human_id || "—"}</p>
                        <p className="text-xs text-muted-foreground">{c.nombre || "—"} · {c.rut || "—"}</p>
                      </td>
                      <td className="py-3 px-4">
                        {c.entry_round >= 2 ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold bg-blue-50 text-blue-800 border border-blue-200 rounded-full px-2 py-0.5"><Users className="w-3 h-3" /> 2ª (recomendantes)</span>
                        ) : <span className="text-xs text-muted-foreground">1ª</span>}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center gap-1 font-semibold ${overdue ? "text-red-700" : "text-slate-700"}`}>
                          {overdue && <AlertTriangle className="w-4 h-4" />}
                          {c.days_pending != null ? `${c.days_pending} día(s)` : "—"}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-semibold" data-testid={`reminder-count-${c.id}`}>{c.reminders_sent}</td>
                      <td className="py-3 px-4 text-muted-foreground text-xs">{fmt(c.last_reminder_at)}</td>
                      <td className="py-3 px-4 text-muted-foreground text-xs">{fmt(c.next_reminder_at)}</td>
                      <td className="py-3 px-4">
                        <button onClick={() => navigate(`/admin/solicitud/${c.id}`)} className="text-primary font-semibold hover:underline" data-testid={`reminder-open-${c.id}`}>Revisar →</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
